import { useMemo, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { usePlans } from "../hooks/usePlans";
import { useLocale } from "../i18n/LocaleContext";
import PlanCard from "./PlanCard";
import SubtleBackground from "./SubtleBackground";

const Destinations = () => {
  const { data, source } = usePlans()
  const { t } = useLocale()
  const [search, setSearch] = useState("")
  const [destinationId, setDestinationId] = useState("")

  const destinationName = useMemo(() => {
    const map = new Map(data.destinations.map((d) => [d.id, d.name] as const))
    return (id: string) => t.destNames[id] ?? map.get(id) ?? t.catalog.miscDestination
  }, [data.destinations, t])

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return data.plans.filter((plan) => {
      if (destinationId && plan.destinationId !== destinationId) return false
      if (!needle) return true
      const haystack = `${plan.title} ${destinationName(plan.destinationId)} ${plan.category} ${plan.countries.join(" ")} ${plan.cities.join(" ")}`.toLowerCase()
      return haystack.includes(needle)
    })
  }, [data.plans, destinationId, search, destinationName])

  const updatedAt = new Intl.DateTimeFormat(t.localeTag, { dateStyle: "medium", timeStyle: "short" })
    .format(new Date(data.meta.scrapedAt))

  return (
    <div className="page destinations">
      <SubtleBackground />
      <header className="destinations-header">
        <h1>{t.catalog.title}</h1>
        <p>{t.catalog.sub}</p>
        {data.meta.sample && (
          <p className="banner banner-info" role="status">
            <FaInfoCircle aria-hidden="true" /> {t.catalog.sampleBanner}
          </p>
        )}
        {data.meta.warnings && data.meta.warnings.length > 0 && (
          <p className="banner banner-warn" role="status">
            <FaInfoCircle aria-hidden="true" /> {t.catalog.warningsBanner(data.meta.warnings.length)}
          </p>
        )}
        <p className="meta-line">
          {t.catalog.metaLine(source === "api" ? t.catalog.sourceApi : t.catalog.sourceBundle, updatedAt)}
        </p>
      </header>

      <div className="destinations-filters">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t.catalog.searchPlaceholder}
          aria-label={t.catalog.searchAria}
        />
        <select
          value={destinationId}
          onChange={(e) => setDestinationId(e.target.value)}
          aria-label={t.catalog.filterAria}
        >
          <option value="">{t.catalog.allDestinations}</option>
          {data.destinations.map((d) => (
            <option key={d.id} value={d.id}>
              {t.destNames[d.id] ?? d.name} ({d.planIds.length})
            </option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <p className="empty-state">
          {t.catalog.emptyState}
        </p>
      ) : (
        <div className="plan-grid">
          {filtered.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              destinationName={destinationName(plan.destinationId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Destinations;
